/**
 * Pure helper that maps a stream of {@link PitchEvent}s to an answer
 * option ID for the current exercise. Extracted from Practice.tsx so it
 * can be unit-tested without mounting the page or booting an
 * AudioContext, mirroring how `lesson-completion.ts` was extracted in
 * B.3.
 *
 * Phase 2A C.1 only wires this for `scale-degree-recognition`; interval
 * grading lands in a follow-up.
 */

import type { PitchEvent } from "@music-app/input-mic";
import { detectHeldPitch, gradeScaleDegree } from "@music-app/input-mic";
import type { Exercise } from "@music-app/exercise-generator";

export interface VocalGradingResult {
  /** Answer option ID to submit, e.g. "degree-3". */
  optionId: string;
  /** Held MIDI value the user sang (rounded). */
  midi: number;
  /** Mean cents deviation across the held window. */
  meanCents: number;
}

/**
 * Inspect a sliding window of recent pitch events for the current
 * exercise and return the matching answer option ID, or null if no
 * stable answer has emerged yet.
 *
 * Returns null when:
 *   - No held pitch has been detected yet (window too short, low
 *     clarity, or unstable).
 *   - The held pitch isn't a diatonic scale tone (chromatic answers
 *     are not in scope for scale-degree recognition).
 *   - The matched scale degree isn't represented as an option (e.g.
 *     leading-tone disabled by prefs and the user sang ti).
 *   - The exercise type or its tonic metadata isn't supported (defensive
 *     — meta.tonicMidi is set by the scale-degree generator, but a
 *     future generator might forget; better to no-op than crash).
 */
export function gradeVocalScaleDegree(
  events: ReadonlyArray<PitchEvent>,
  exercise: Exercise,
): VocalGradingResult | null {
  if (exercise.exerciseType !== "scale-degree-recognition") return null;
  const tonicMidi = exercise.meta.tonicMidi;
  const mode = exercise.meta.tonalityMode;
  if (typeof tonicMidi !== "number" || (mode !== "major" && mode !== "minor")) {
    return null;
  }
  const held = detectHeldPitch(events);
  if (!held) return null;
  const match = gradeScaleDegree(held.midi, tonicMidi, mode);
  if (!match) return null;
  // Map degree N → "degree-N", which is the ID convention the
  // scale-degree generator uses for AnswerOption.id. Defensive lookup
  // against `exercise.options` so a future ID change is detected here
  // rather than as a silent missed grade.
  const optionId = `degree-${match.degree}`;
  if (!exercise.options.some((o) => o.id === optionId)) return null;
  return {
    optionId,
    midi: held.midi,
    meanCents: held.meanCents,
  };
}
